// QuickBooks Online -- turning a Stede vendor invoice into a QuickBooks bill.
//
// Session 78n9.2, Issue #298 / part of #395.
// Gate: docs/gates/Stede_DocumentOutput_Gate_395.html, Screen 4, decisions
// D6 (the API is the path, there is no file fallback) and callout N (the
// supplier is remembered by ID, never by name).
//
// WHAT THIS IS, in plain English: Bridgette has typed a vendor's invoice into
// Stede. This turns that invoice into exactly the shape QuickBooks wants for a
// bill, and -- just as importantly -- says why it CANNOT when something is
// missing, before anything is sent.
//
// This file is deliberately pure: no network, no Supabase, no React. Every
// value it needs is handed in. That is what makes it testable, and this is the
// one piece of the QuickBooks work where a subtle error writes a wrong dollar
// amount into an accounting system rather than drawing a wrong pixel.

// ---------------------------------------------------------------------------
// MONEY. Every amount is carried as an integer number of cents inside this
// file and converted back exactly once, on the way out.
//
// This is not defensive habit. 0.1 + 0.2 is 0.30000000000000004 in JavaScript,
// and an invoice with forty lines is forty chances to accumulate that. The
// figure QuickBooks receives has to equal the figure on the screen Bridgette
// approved, to the cent, or the three-way match this whole module serves is
// comparing two different numbers.
// ---------------------------------------------------------------------------
export function toCents(v) {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (!Number.isFinite(n)) return 0;
  // Round half away from zero. Math.round(-0.5) is -0, which would turn a
  // half-cent credit into a zero-cent one.
  return n < 0 ? -Math.round(Math.abs(n) * 100) : Math.round(n * 100);
}

export function fromCents(cents) {
  // Two decimal places, as a number rather than a string: QuickBooks' JSON
  // takes Amount as a number, and a string is accepted-then-coerced, which is
  // a silent behaviour to avoid depending on.
  return Math.round(cents) / 100;
}

function num(v) {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

function money(cents) {
  const sign = cents < 0 ? '-' : '';
  const a = Math.abs(cents);
  return sign + '$' + Math.floor(a / 100).toLocaleString('en-US') + '.' + String(a % 100).padStart(2, '0');
}

// ---------------------------------------------------------------------------
// Blockers.
//
// A blocker is a reason this one document cannot be sent. It is returned
// rather than thrown, because Screen 4's whole design is a per-row state --
// two invoices selected, one matched and one not, and the matched one still
// sends. An exception would take the whole selection down with it.
//
// Every blocker carries a message written for Bridgette, not for a log. The
// gate's rule G applies: block the button and say why, rather than failing the
// click.
// ---------------------------------------------------------------------------
export const BLOCK = {
  ALREADY_EXPORTED: 'already_exported',
  NO_VENDOR: 'no_vendor',
  NO_SUPPLIER_MATCH: 'no_supplier_match',
  INTERNAL_VENDOR: 'internal_vendor',
  NO_LINES: 'no_lines',
  NO_ACCOUNT: 'no_account',
  TOTALS_DISAGREE: 'totals_disagree',
  TAX_UNMAPPED: 'tax_unmapped',
  NO_DATE: 'no_date',
  // #430, Session 78n9.4. The only blocker here that a person can CLEAR rather
  // than fix. Every other one names something that must change before the bill
  // is honest; this one names a judgement Bridgette is entitled to make.
  UNRESOLVED_DISCREPANCY: 'unresolved_discrepancy',
};

function blocker(code, message) {
  return { code, message };
}

// ---------------------------------------------------------------------------
// Account resolution, and the order is the design.
//
// A QuickBooks bill line is refused outright without an expense account, so
// every line has to land somewhere. The order of preference is:
//
//   1. the account mapped to the item's own category
//   2. the company-wide default account
//
// and if neither exists the document is blocked rather than guessed at. There
// is deliberately no third fallback to "the first account we can see":
// posting a materials purchase to whatever account sorted first is the kind of
// error that is invisible for a month and then has to be unpicked by an
// accountant.
//
// An off-catalog line has no item and therefore no category, so it always
// takes the company default. That is not a gap -- an off-catalog purchase is
// by definition one nobody has classified yet.
// ---------------------------------------------------------------------------
export function resolveAccount(line, defaults) {
  const cat = (line && line.item && line.item.category) || null;
  if (cat && cat.quickbooks_account_id) {
    return {
      id: String(cat.quickbooks_account_id),
      name: cat.quickbooks_account_name || null,
      source: 'category',
      categoryName: cat.name || null,
    };
  }
  if (defaults && defaults.accountId) {
    return {
      id: String(defaults.accountId),
      name: defaults.accountName || null,
      source: 'default',
      categoryName: (cat && cat.name) || null,
    };
  }
  return null;
}

// A one-line human description for a bill line. QuickBooks shows this on the
// bill and on every report that reaches it, so it carries what the item was
// and how many -- not an id nobody outside Stede can resolve.
export function lineDescription(line) {
  const name = (line && line.item && line.item.name)
    || (line && line.off_catalog_description)
    || 'Item';
  const qty = num(line && line.qty_invoiced);
  const uom = (line && line.item && line.item.uom) || (line && line.off_catalog_uom) || '';
  if (qty > 0) {
    return name + ' - ' + String(qty) + (uom ? ' ' + uom : '');
  }
  return name;
}

// ---------------------------------------------------------------------------
// The provenance note.
//
// Written onto every document Stede creates in QuickBooks so that somebody
// looking at a bill inside QuickBooks six months from now can find the order
// it came from. Without this the bill is an orphan: QuickBooks has no idea
// Stede exists, and the only link back would be a vendor name and a date.
// ---------------------------------------------------------------------------
export function privateNote(request, doc) {
  const parts = [];
  if (request && request.request_number) parts.push('Stede ' + request.request_number);
  if (request && request.po_ref) parts.push('PO ' + request.po_ref);
  if (request && request.work_order_ref) parts.push('WO ' + request.work_order_ref);
  if (doc && doc.payment_terms) parts.push('Terms: ' + doc.payment_terms);
  return parts.join(' - ');
}

// ---------------------------------------------------------------------------
// The builder.
//
// candidate = {
//   doc:      a material_request_invoices row -- document_type, invoice_ref,
//             invoice_date, due_date, payment_terms, subtotal, tax_amount,
//             total_amount, exported_at, quickbooks_bill_id
//   request:  the material_requests row it belongs to
//   vendor:   the inventory_vendors row, carrying quickbooks_vendor_id
//   lines:    [{ line_amount, qty_invoiced, item, off_catalog_description,
//                off_catalog_uom }]  -- item carries { name, uom, category }
//   defaults: { accountId, accountName } from company_settings
// }
//
// Returns { kind, payload, blockers, warnings, totals }.
// payload is null whenever there is a blocker: there is no such thing as a
// partially valid bill, and returning one invites a caller to send it.
// ---------------------------------------------------------------------------
// opts.acceptVariance — #430, Session 78n9.4. Bridgette ticking past an
// unresolved discrepancy. It is a SECOND ARGUMENT rather than a field on the
// candidate deliberately: the candidate is what the server READ, and this is
// what the caller CHOSE. Mixing them would let a client-supplied flag ride in
// alongside server-read rows and be mistaken for one.
export function buildBillPayload(candidate, opts) {
  const acceptVariance = !!(opts && opts.acceptVariance);
  const doc = (candidate && candidate.doc) || null;
  const request = (candidate && candidate.request) || null;
  const vendor = (candidate && candidate.vendor) || null;
  const lines = Array.isArray(candidate && candidate.lines) ? candidate.lines : [];
  const defaults = (candidate && candidate.defaults) || null;

  const isCredit = !!doc && doc.document_type === 'credit';
  // QuickBooks carries a vendor credit as its own object rather than as a
  // negative bill, which is exactly why #299 stores both amounts positive.
  // The sign lives in the object type, here, and nowhere else.
  const kind = isCredit ? 'VendorCredit' : 'Bill';

  const blockers = [];
  const warnings = [];

  if (!doc) {
    return {
      kind,
      payload: null,
      blockers: [blocker(BLOCK.NO_LINES, 'There is no invoice to send.')],
      warnings,
      totals: null,
    };
  }

  if (doc.exported_at || doc.quickbooks_bill_id) {
    blockers.push(blocker(
      BLOCK.ALREADY_EXPORTED,
      (doc.invoice_ref || 'This invoice')
        + ' was already sent to QuickBooks. Sending it again would create a duplicate bill.',
    ));
  }

  if (!vendor) {
    blockers.push(blocker(BLOCK.NO_VENDOR, 'This order has no vendor, so there is nobody to bill it to.'));
  } else if (vendor.is_internal) {
    // Found in the live test at 78n9.3, and it was a dead end rather than a
    // wrong message. An internal vendor is another D&R branch — Houston
    // supplying Dallas — which is a stock transfer, not a purchase. D&R does
    // not bill itself, so there is no QuickBooks supplier and there never will
    // be one.
    //
    // Without this it fell to NO_SUPPLIER_MATCH below, which told Bridgette to
    // "pick the matching supplier" for a vendor the Suppliers tab deliberately
    // does not list. She would have hunted for a row that cannot exist. A
    // blocker that sends somebody looking for something unfindable is worse
    // than one that says nothing.
    blockers.push(blocker(
      BLOCK.INTERNAL_VENDOR,
      '"' + (vendor.name || 'This vendor') + '" is another D&R branch, not an outside supplier.'
        + ' A transfer between branches is not a bill, so it does not go to QuickBooks.',
    ));
  } else if (!vendor.quickbooks_vendor_id) {
    // Callout N. This is the NO MATCH state on Screen 4, and it is a one-time
    // discovery per vendor rather than a per-bill lookup.
    blockers.push(blocker(
      BLOCK.NO_SUPPLIER_MATCH,
      '"' + (vendor.name || 'This vendor') + '" is not linked to a supplier in QuickBooks yet.'
        + ' Pick the matching supplier, or create it in QuickBooks first.',
    ));
  }

  if (!doc.invoice_date) {
    blockers.push(blocker(BLOCK.NO_DATE, 'This invoice has no date, and QuickBooks dates every bill.'));
  }

  // #430, Session 78n9.4. An invoice that never matched its order was pushable
  // in silence until this session, and it is not hypothetical: INV-74Z5-OVER
  // is QuickBooks bill 148, $200.00 carrying $150.00 of unexplained variance,
  // sent at 78n9.3 with nothing on screen to mention it.
  //
  // SAP blocks this outright -- a variance outside tolerance sets Blocked for
  // Payment and the invoice waits. Stede deliberately does NOT, and the reason
  // is a difference in what sits behind the block: SAP has a whole variance
  // resolution workflow and a team; D&R has Bridgette and one Resolve action,
  // so a hard block means a $3 rounding difference stops the month closing.
  //
  // So it is the one blocker a person can clear by deciding to. What makes
  // that safe rather than decorative is that the decision is taken HERE, on
  // rows the server read, and recorded -- not asserted by the screen.
  const varianceFlagged = !!doc.discrepancy_flagged && !doc.discrepancy_resolved;
  const varianceCents = toCents(doc.discrepancy_amount);
  if (varianceFlagged && !acceptVariance) {
    blockers.push(blocker(
      BLOCK.UNRESOLVED_DISCREPANCY,
      (doc.invoice_ref || 'This invoice') + ' never matched its order and nobody has resolved it'
        + (varianceCents ? ', a difference of ' + money(varianceCents) : '')
        + '. Sending it enters the full ' + money(toCents(doc.total_amount))
        + ' in QuickBooks as money D&R owes.',
    ));
  } else if (varianceFlagged) {
    warnings.push(
      'Sent with an unresolved discrepancy'
      + (varianceCents ? ' of ' + money(varianceCents) : '') + ', accepted deliberately.'
    );
  }

  if (lines.length === 0) {
    blockers.push(blocker(BLOCK.NO_LINES, 'This invoice has no lines on it, so there is nothing to bill.'));
  }

  // Build the lines and total them in cents.
  const qbLines = [];
  let linesCents = 0;
  let missingAccount = 0;
  let usedDefault = 0;

  for (const line of lines) {
    const cents = toCents(line && line.line_amount);
    linesCents += cents;

    const account = resolveAccount(line, defaults);
    if (!account) {
      missingAccount += 1;
      continue;
    }
    if (account.source === 'default') usedDefault += 1;

    qbLines.push({
      DetailType: 'AccountBasedExpenseLineDetail',
      Amount: fromCents(cents),
      Description: lineDescription(line),
      AccountBasedExpenseLineDetail: {
        AccountRef: account.name
          ? { value: account.id, name: account.name }
          : { value: account.id },
        // Every line on a D&R bill is a materials purchase billable to nobody.
        // Saying so explicitly stops QuickBooks defaulting it to Billable and
        // surfacing these on a customer invoice later.
        BillableStatus: 'NotBillable',
      },
    });
  }

  if (missingAccount > 0) {
    blockers.push(blocker(
      BLOCK.NO_ACCOUNT,
      missingAccount === lines.length
        ? 'No expense account is set for these items, and QuickBooks refuses a bill line without one.'
          + ' Set a company default account, or map the item categories.'
        : missingAccount + ' of ' + lines.length + ' lines have no expense account.'
          + ' QuickBooks refuses a bill line without one.',
    ));
  }

  // The invariant, and it is a real one rather than an arbitrary tolerance:
  // enter_material_request_invoice() computes subtotal as the sum of the very
  // line prices these lines carry, so the two agreeing to the cent is
  // guaranteed by construction. If they ever disagree, something upstream is
  // wrong and the right answer is to refuse to post, not to pick a number.
  const subtotalCents = toCents(doc.subtotal);
  if (lines.length > 0 && linesCents !== subtotalCents) {
    blockers.push(blocker(
      BLOCK.TOTALS_DISAGREE,
      'The lines on this invoice add up to ' + money(linesCents)
        + ' but the invoice total says ' + money(subtotalCents)
        + '. Stede will not send a bill it cannot reconcile.',
    ));
  }

  // Sales tax. D&R buys materials on a resale certificate so this is normally
  // null (#286) -- but when it is not, there is nowhere honest to put it.
  // Stede maps categories to expense accounts and has no tax account of any
  // kind, and folding tax into a materials account misstates both. Blocking
  // one row and naming why is the honest failure; guessing is not.
  const taxCents = toCents(doc.tax_amount);
  if (taxCents !== 0) {
    blockers.push(blocker(
      BLOCK.TAX_UNMAPPED,
      'This invoice carries ' + money(taxCents) + ' of sales tax and Stede has no QuickBooks'
        + ' account to book tax to. Enter this one in QuickBooks by hand.',
    ));
  }

  if (!doc.invoice_ref) {
    // Not a blocker: QuickBooks accepts a bill with no reference number. It is
    // a warning because the duplicate check on the next send has nothing to
    // match on without it.
    warnings.push('This invoice has no reference number, so QuickBooks cannot warn you if it is sent twice.');
  }
  if (usedDefault > 0 && usedDefault < qbLines.length) {
    warnings.push(usedDefault + ' of ' + qbLines.length + ' lines use the company default expense account.');
  } else if (usedDefault > 0 && usedDefault === qbLines.length) {
    warnings.push('Every line uses the company default expense account.');
  }

  const totals = {
    linesCents,
    subtotalCents,
    taxCents,
    totalCents: toCents(doc.total_amount),
    lineCount: lines.length,
  };

  // #430. Returned on BOTH exits, blocked and clean, so the Send sheet can draw
  // the tick from a preview and the caller can record what was accepted without
  // re-deriving either from the raw row.
  const variance = {
    flagged: varianceFlagged,
    cents: varianceCents,
    accepted: varianceFlagged && acceptVariance,
  };

  if (blockers.length > 0) {
    return { kind, payload: null, blockers, warnings, totals, variance };
  }

  const payload = {
    VendorRef: { value: String(vendor.quickbooks_vendor_id) },
    TxnDate: doc.invoice_date,
    Line: qbLines,
    PrivateNote: privateNote(request, doc),
  };

  // DocNumber is the vendor's own invoice number. QuickBooks uses it for its
  // duplicate warning, which is the second line of defence behind Stede's own
  // exported_at check.
  if (doc.invoice_ref) payload.DocNumber = String(doc.invoice_ref);

  // A vendor credit has no due date -- nothing is owed on it.
  if (!isCredit && doc.due_date) payload.DueDate = doc.due_date;

  return { kind, payload, blockers, warnings, totals, variance };
}

// The endpoint a built payload posts to, given the connected company. Kept
// here beside the builder so the object and the resource it creates cannot
// drift apart.
export function qboResourcePath(kind) {
  return kind === 'VendorCredit' ? 'vendorcredit' : 'bill';
}
